import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isEmpty, map } from "lodash";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/ui/spinner";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import ClickableLink from "@/components/ui/clickable-link";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  ArrowUpRight,
  Job,
  Mail,
  Phone,
  PeopleBadge
} from "@dreamhub/designsystem/icons";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { ComputedFields } from "@/services/api/types";
import { usePartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import {
  PartnerContactColumn,
  PartnerContactData
} from "@/pages/partners/contacts/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";

const PEOPLE_PAGE_COUNT = 5;

interface RowProps {
  contact: PartnerContactData;
  onOpen: (id: string) => void;
}

const PartnerContactRow: React.FC<RowProps> = ({ contact, onOpen }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const url = `/${entityTypeToEntityName[EntityType.PARTNER_CONTACT]}/${contact.id}`;
  const phones = [contact.workPhone, contact.mobilePhone].filter(Boolean);

  return (
    <div
      onClick={() => onOpen(contact.id)}
      className="relative flex w-full group items-start cursor-pointer transition-colors duration-200 ease-in-out rounded-xl hover:bg-white-4"
    >
      <div className="w-full min-w-0 p-2 flex flex-col gap-2">
        <ClickableLink href={url} onClick={() => onOpen(contact.id)}>
          <Text
            variant="label"
            className="hover:text-link transition-colors ease-in-out duration-200 cursor-pointer pl-[6px]"
          >
            {getPartnerContactFullName(contact)}
          </Text>
        </ClickableLink>
        <div className="flex flex-col gap-1">
          {contact.jobTitle && (
            <Badge
              text={contact.jobTitle}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Job />
                </IconContainer>
              }
            />
          )}
          {map(contact.emails ?? [], (emailAddress, emailIndex) => (
            <Badge
              key={`${emailAddress}-${emailIndex}`}
              text={emailAddress}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="min-h-6 py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Mail />
                </IconContainer>
              }
              copyable
              copyLabels={{
                tooltip: t("copyEmailTooltip"),
                copied: t("copied")
              }}
            />
          ))}
          {map(phones, (phone, index) => (
            <Badge
              key={`${phone}-${index}`}
              text={phone as string}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="min-h-6 py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Phone />
                </IconContainer>
              }
              copyable
              copyLabels={{
                tooltip: t("copyPhoneTooltip"),
                copied: t("copied")
              }}
            />
          ))}
        </div>
      </div>
      <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100">
        <Tooltip>
          <TooltipTrigger>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(url);
              }}
              variant="icon"
              size="md"
            >
              <ArrowUpRight />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>{t("detailedViewTooltip")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

interface Props {
  partnerId: string | undefined;
}

const PartnerPeopleSection: React.FC<Props> = ({ partnerId }) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(PEOPLE_PAGE_COUNT);

  useEffect(() => {
    setVisibleCount(PEOPLE_PAGE_COUNT);
  }, [partnerId]);

  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const handleOpen = useCallback(
    (id: string) => newOpenDrawer(id, EntityType.PARTNER_CONTACT),
    [newOpenDrawer]
  );

  const filters = useMemo(
    () => ({ [PartnerContactColumn.PARTNER]: [partnerId ?? ""] }),
    [partnerId]
  );

  const { entities: contacts, isLoading } = usePartnerContactsInfinite(
    filters,
    undefined,
    undefined,
    undefined,
    "firstName",
    true,
    false,
    !!partnerId,
    ComputedFields.MINIMAL
  );

  const shouldShowMore = contacts.length > visibleCount;
  const visibleContacts = contacts.slice(0, visibleCount);

  const handleShowMore = () =>
    setVisibleCount((prev) =>
      Math.min(prev + PEOPLE_PAGE_COUNT, contacts.length)
    );

  return (
    <>
      <div className="flex flex-col gap-4 border-[0.5px] border-border-border1 bg-surface-elevation2 w-full p-4 rounded-xl">
        <Text variant="lgLabel">{t("partners.partnerPeople")}</Text>
        {isLoading ? (
          <div className="flex w-full h-full items-center justify-center">
            <Spinner size="5" />
          </div>
        ) : isEmpty(visibleContacts) ? (
          <div className="p-2 flex flex-col items-center">
            <IconContainer className="size-14">
              <PeopleBadge />
            </IconContainer>
            <Text variant="bodyTertiary">
              {t("partners.partnerPeopleEmpty")}
            </Text>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="flex flex-col gap-2">
              {map(visibleContacts, (contact) => (
                <PartnerContactRow
                  key={contact.id}
                  contact={contact}
                  onOpen={handleOpen}
                />
              ))}
            </div>
            {shouldShowMore && (
              <Button
                variant="tertiary"
                className="w-fit h-fit self-end mt-2"
                onClick={handleShowMore}
              >
                {t("showNumMore", {
                  num:
                    contacts.length - visibleCount > PEOPLE_PAGE_COUNT
                      ? PEOPLE_PAGE_COUNT
                      : contacts.length - visibleCount
                })}
              </Button>
            )}
          </div>
        )}
      </div>
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.PARTNER_CONTACT}
        onClose={newCloseDrawer}
      />
    </>
  );
};

export default PartnerPeopleSection;
